import type { PermissionEnvelope, PermissionMap } from '../types/permissions'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const flattenBooleanBranches = (
  source: Record<string, unknown>,
  prefix = '',
  accumulator: PermissionMap = {},
): PermissionMap => {
  Object.entries(source).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key

    if (typeof value === 'boolean') {
      accumulator[nextKey] = value
      return
    }

    if (isRecord(value)) {
      flattenBooleanBranches(value, nextKey, accumulator)
    }
  })

  return accumulator
}

const isBooleanBranchRecord = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value)) {
    return false
  }

  const entries = Object.values(value)
  if (entries.length === 0) {
    return false
  }

  return entries.every((entry) => typeof entry === 'boolean' || isBooleanBranchRecord(entry))
}

const dedupeStrings = (items: string[]) => Array.from(new Set(items.filter(Boolean)))

export const normalizeAppMenuFeatures = (source: unknown): string[] => {
  if (Array.isArray(source)) {
    return dedupeStrings(
      source.flatMap((item) => {
        if (typeof item === 'string') {
          return item
        }

        if (isRecord(item)) {
          return normalizeAppMenuFeatures(item)
        }

        return []
      }),
    )
  }

  if (!isRecord(source)) {
    return []
  }

  if (typeof source.feature === 'string') {
    return [source.feature]
  }

  if (Array.isArray(source.items)) {
    return dedupeStrings(source.items.flatMap((item) => normalizeAppMenuFeatures(item)))
  }

  if (Array.isArray(source.menu)) {
    return dedupeStrings(source.menu.flatMap((item) => normalizeAppMenuFeatures(item)))
  }

  return []
}

export const normalizePermissionMap = (source: unknown): PermissionMap => {
  if (!source) {
    return {}
  }

  if (isRecord(source) && isRecord(source.permissions)) {
    return flattenBooleanBranches(source.permissions)
  }

  if (isBooleanBranchRecord(source)) {
    return flattenBooleanBranches(source)
  }

  return {}
}

export const mergePermissionMaps = (...sources: PermissionMap[]): PermissionMap =>
  sources.reduce<PermissionMap>((accumulator, source) => ({ ...accumulator, ...source }), {})

export const normalizeAllowedActions = (source: unknown): string[] => {
  if (!source) {
    return []
  }

  if (Array.isArray(source)) {
    return dedupeStrings(source.map((item) => String(item)))
  }

  if (typeof source === 'string') {
    return source ? [source] : []
  }

  if (isRecord(source) && 'allowed_actions' in source) {
    return normalizeAllowedActions(source.allowed_actions)
  }

  if (isRecord(source)) {
    const flattenedBooleanMap = flattenBooleanBranches(source)
    const truthyActions = Object.entries(flattenedBooleanMap)
      .filter(([, isAllowed]) => isAllowed)
      .map(([actionKey]) => actionKey)

    if (truthyActions.length > 0) {
      return dedupeStrings(truthyActions)
    }
  }

  return []
}

export const normalizePermissionEnvelope = <T>(data: T): PermissionEnvelope<T> => ({
  data,
  permissions: normalizePermissionMap(data),
  allowedActions: normalizeAllowedActions(data),
})
